import { useEffect } from "react";
import catalogue from "./analyticsSearchCatalogue.json";

export function normalizeSearch(text) {
  return String(text || "").replace(/([a-z])([A-Z])/g, "$1 $2")
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}
const groups = [
  ["rm", "raw material"], ["qa", "qc", "quality", "antibiotic"],
  ["po", "purchase order", "orders"], ["dispatch", "shipment"],
  ["stock", "inventory", "cold store"], ["renewal", "renewals", "certificate", "certification"],
];
function searchText(item) {
  let text = normalizeSearch(`${item.title} ${item.tabLabel} ${item.keywords}`);
  for (const group of groups) {
    if (group.some(term => (` ${text} `).includes(` ${term} `))) text += " " + group.join(" ");
  }
  // The farm audit endpoint is displayed under its actual plant-audit title.
  if (item.target === "ceo-farm-audit-schedule") text += " farm audit schedule";
  return text;
}
const indexed = catalogue.map(item => ({ ...item, searchText: searchText(item) }));
function nearWord(term, word) {
  if (term.length < 4 || Math.abs(term.length - word.length) > 1) return false;
  if (term.length === word.length) {
    const diffs = [...term].map((c, i) => c === word[i] ? -1 : i).filter(i => i >= 0);
    return diffs.length <= 1 || (diffs.length === 2 && diffs[1] === diffs[0] + 1 &&
      term[diffs[0]] === word[diffs[1]] && term[diffs[1]] === word[diffs[0]]);
  }
  const [short, long] = term.length < word.length ? [term, word] : [word, term];
  for (let i = 0; i < long.length; i++) if (long.slice(0, i) + long.slice(i + 1) === short) return true;
  return false;
}
export function findAnalytics(query, allowAI = false) {
  const normalized = normalizeSearch(query).slice(0, 160);
  const terms = [...new Set(normalized.split(" ").filter(Boolean))];
  const items = indexed.filter(item => allowAI || item.tab !== "ai");
  if (!terms.length) return items.filter(item => item.kind === "Tab");
  return items.map(item => {
    const words = item.searchText.split(" ");
    const hits = terms.map(term => words.some(word => word.includes(term)) ? 2 : words.some(word => nearWord(term, word)) ? 1 : 0);
    const title = normalizeSearch(item.title);
    const matched = hits.filter(Boolean).length;
    return { ...item, partial: matched < terms.length,
      score: matched ? hits.reduce((a, b) => a + b, 0) * 10 +
        (matched === terms.length ? 100 : 0) + (title === normalized ? 200 : title.includes(normalized) ? 50 : 0) : 0 };
  }).filter(item => item.score > 0).sort((a, b) => b.score - a.score || a.title.localeCompare(b.title) || a.tabLabel.localeCompare(b.tabLabel));
}
export function analyticsDestination(item, location) {
  const params = new URLSearchParams(location.pathname === "/ceo-command-center" ? location.search : "");
  params.set("tab", item.tab);
  return { pathname: "/ceo-command-center", search: "?" + params.toString(), hash: "#analytics-" + encodeURIComponent(item.target) };
}
export function useAnalyticsSearchTarget(active, location) {
  useEffect(() => {
    if (!location.hash.startsWith("#analytics-")) return;
    const requestedTab = new URLSearchParams(location.search).get("tab") || "overview";
    if (requestedTab !== active) return;
    let target;
    try { target = decodeURIComponent(location.hash.slice(11)); } catch { return; }
    if (!catalogue.some(item => item.tab === active && item.target === target)) return;
    let timer, highlighted;
    const frame = requestAnimationFrame(() => {
      const element = [...document.querySelectorAll("[data-analytics-target]")]
        .find(node => node.getAttribute("data-analytics-target") === target);
      if (!element) return;
      highlighted = element;
      element.classList.add("analytics-search-highlight");
      const oldTabIndex = element.getAttribute("tabindex");
      element.setAttribute("tabindex", "-1");
      element.focus({ preventScroll: true });
      element.scrollIntoView({ block: "start", behavior: "auto" });
      if (oldTabIndex === null) element.removeAttribute("tabindex");
      else element.setAttribute("tabindex", oldTabIndex);
      timer = setTimeout(() => element.classList.remove("analytics-search-highlight"), 3500);
    });
    return () => { cancelAnimationFrame(frame); clearTimeout(timer); highlighted?.classList.remove("analytics-search-highlight"); };
  }, [active, location.hash, location.search, location.key]);
}
